import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import confetti from 'canvas-confetti';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import {
  AlertDialogComponent,
  AlertDialogData,
} from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { WelcomeDialogComponent } from '../../../shared/components/dialog/welcome-modal/welcome-dialog.component';
import { WorkExperienceComponent } from '../../../shared/components/dialog/work-experience/work-experience.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';
import { UrlParserService } from '../../../shared/services/url-parser.service';

interface EventType {
  id: string;
  eventName: string;
  eventDate: string;
}

interface APIResponse {
  status: boolean;
  message: string;
  data: any;
}

@Component({
  selector: 'app-teacher-dashboard',
  standalone: true,
  imports: [TableComponent, CommonModule, ButtonComponent],
  templateUrl: './teacher.component.html',
  styleUrls: ['./teacher.component.scss'],
})
export class TeacherComponent implements AfterViewInit {
  @ViewChild('confettiCanvas', { static: true })
  confettiCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
  @ViewChild(TableComponent) tableComponent!: TableComponent;

  isEditPage = false;
  cardEvents: any[] = [];
  loading = false;
  tableSegment: string = '';
  private currentRequestId = 0;

  columns: string[] = [
    'Event name',
    'Event type',
    'Date & time',
    'status',
    'Location',
  ];
  secondaryColumn: string[] = [
    'Event name',
    'Event type',
    'Date & time',
    // 'status',
    'Location',
    'number of students',
    'status',
    //   'teacher approval'
  ];
  studentColumn: string[] = [
    'Event name',
    'Event type',
    'Student name',
    'Date & time',
    'Location',
    'status',
  ];
  data: any[] = [];
  upcomingEvent: any[] = [];
  fullData: any;
  user = {
    name: '',
    email: '',
  };

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private httpService: HttpService,
    private snackBar: MatSnackBar,
    private urlParser: UrlParserService,
  ) {
    this.route.url.subscribe((segments) => {
      this.isEditPage = segments.some((segment) => segment.path === 'edit');
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const isFirstLogin =
        localStorage.getItem('teacherIsFirstLogin') === 'true';
      if (isFirstLogin) {
        this.dialog.open(WelcomeDialogComponent, {
          width: '1100px',
          height: '660px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          disableClose: true,
          data: { label: 'Teacher Portal!' },
        });
        this.launchConfetti();
        localStorage.setItem('teacherIsFirstLogin', 'false');
      }
    }, 200);
  }

  ngOnInit(): void {
    const UserDetailsStr = localStorage.getItem('userDetails') || '';
    let name = '';
    try {
      const UserDetails = UserDetailsStr ? JSON.parse(UserDetailsStr) : null;
      if (UserDetails) {
        name =
          (UserDetails.firstName || '') + ' ' + (UserDetails.lastName || '');
      }
    } catch (e) {
      name = '';
    }
    this.user.name = name.trim();
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab && ['admin', 'teacher', 'student'].includes(tab)) {
        this.tableSegment = tab;
      } else {
        this.tableSegment = 'admin';
      }
      this.fetchEvents();
    });
    // fetchEvents() is already triggered by the queryParams subscription above (it emits the
    // current params synchronously), so calling it again here would just duplicate the request.
    this.fetchUpcomingEvents();
  }

  launchConfetti() {
    if (!this.confettiCanvas) return;
    const myConfetti = confetti.create(this.confettiCanvas.nativeElement, {
      resize: true,
      useWorker: true,
    });
    myConfetti({
      particleCount: 520,
      spread: 520,
      origin: { y: 0.2 },
      colors: [
        '#f9d423',
        '#e65c00',
        '#7ed6df',
        '#e056fd',
        '#f6e58d',
        '#ff7979',
      ],
    });
    setTimeout(() => this.rainConfetti(myConfetti), 800);
  }

  rainConfetti(myConfetti: any) {
    let interval = setInterval(() => {
      myConfetti({
        particleCount: 10,
        spread: 90,
        startVelocity: 45,
        origin: { x: Math.random(), y: -0.1 },
      });
    }, 220);
    setTimeout(() => clearInterval(interval), 2200);
  }

  private adjustPaginationAfterDelete(): void {
    const totalPages = Math.ceil(
      this.data.length / (this.tableComponent?.pageSize || 10),
    );
    if (
      this.tableComponent &&
      this.tableComponent.currentPage > totalPages &&
      totalPages > 0
    ) {
      this.tableComponent.currentPage = totalPages;
    }
  }

  onViewAdmin(row: any): void {
    const RowId = row?.eventId || row?.id;

    this.router.navigate(['/events/event-list/view', RowId], {
      queryParams: { segment: 'admin' },
    });
  }

  onView(row: any): void {
    const RowId = row?.eventId || row?.id;

    if (!RowId) {
      console.error('Missing event ID', { row });
      return;
    }
    const segment =
      this.tableSegment || row?.source || row?.createdBy || 'admin';
    const creatorRole =
      typeof segment === 'string' ? segment.toLowerCase() : 'admin';
    const encodedId = this.urlParser.encodeId(RowId.toString());
    const encodedSegment = this.urlParser.encodeId(creatorRole);
    this.router.navigate(['/events/event-list/view', encodedId], {
      queryParams: { segment: encodedSegment },
    });
  }

  onDelete(row: any): void {
    const dialogData: AlertDialogData = {
      title: 'Confirm delete',
      message: `Are you sure want to delete?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog
      .open(AlertDialogComponent, { width: '25.438rem', data: dialogData })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.httpService
            .delete<APIResponse>(`teacherevent/delete/${row.id}`)
            .subscribe({
              next: (response) => {
                if (response.status) {
                  this.data = this.data.filter(
                    (s: { id: any }) => s.id !== row.id,
                  );
                  this.cardEvents = this.cardEvents.filter(
                    (s) => s.eventId !== row.id && s.id !== row.id,
                  );

                  this.adjustPaginationAfterDelete();
                  this.showSuccessToast(response.message);
                  this.fetchUpcomingEvents();
                } else {
                  this.showErrorToast(response.message);
                }
              },
              error: (err) => this.showErrorToast(err?.message),
            });
        }
      });
  }

  onEdit(row: any): void {
    const fullEventData = this.cardEvents.find(
      (event) => event.eventId === row.id || event.id === row.id,
    );

    if (fullEventData) {
      this.openWorkExperienceDialog(fullEventData, true);
    } else {
      console.error('Event data not found for editing');
      this.showErrorToast('Event data not found for editing');
    }
  }

  fetchEvents(tab?: string): void {
    this.loading = true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let segment = tab || this.tableSegment;
    const requestId = ++this.currentRequestId;
    this.httpService
      .get<APIResponse>(`teacherevent/my-events?source=${segment}`)
      .subscribe({
        next: (response) => {
          this.cardEvents = response.data?.items;
          if (requestId !== this.currentRequestId) {
            return;
          }
          this.data = response.data?.items.map((item: any) => {
            const startDate = new Date(item?.eventStartDate);
            const endDate = item?.eventEndDate
              ? new Date(item.eventEndDate)
              : null;

            const formattedStartDate = startDate.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            });

            const formattedEndDate = endDate
              ? endDate.toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : null;

            const formattedDate =
              formattedEndDate && formattedStartDate !== formattedEndDate
                ? `${formattedStartDate} - ${formattedEndDate}`
                : formattedStartDate;

            const rawStatus =
              this.tableSegment === 'admin'
                ? item?.eventStatus
                : this.tableSegment === 'student'
                  ? item?.eventTeacherApprovalStatus
                  : item?.eventAdminApprovalStatus;
            return {
              id: item?.eventId,
              'Event name': item?.eventName,
              'Event type': item?.eventTypeName ? item?.eventTypeName : '-',
              'Student name': item?.studentName || '-',
              'Date & time': {
                date: formattedDate,
                time: item?.eventTime,
              },
              // SORT VALUE
              _dateTimeSort: startDate.getTime(),
              'Approval status': item?.eventAdminApprovalStatusName,
              // status: item?.eventStatus,
              // status:
              //   !item?.isDraft && item.eventStatus == 'Draft'
              //     ? 'Upcoming'
              //     : item?.isDraft
              //       ? 'Draft'
              //       : (item.eventStatus ?? 'Active'),
              Location: item?.location,
              status:
                this.tableSegment === 'admin'
                  ? this.normalizeAdminEventStatus(rawStatus)
                  : String(rawStatus)?.toLowerCase() === 'pending'
                    ? 'Awaiting Approval'
                    : rawStatus,
              statusClass:
                this.tableSegment === 'admin'
                  ? this.getStatusClass(
                      this.normalizeAdminEventStatus(rawStatus),
                    )
                  : this.tableSegment === 'student' &&
                      String(rawStatus)?.toLowerCase() === 'declined'
                    ? 'status-inactive'
                    : '',
              'admin approval': item?.eventAdminApprovalStatus ?? '-',
              'number of students': item?.studentCount ?? '-',
              'teacher approval': item?.eventTeacherApprovalStatus ?? '-',
              isStatusClickable: false,
              isAdminClickable: false,
              isTeacherClickable: false,
            };
          });
        },
        error: (err) => {
          this.loading = false;
          if (
            err?.status === 403 ||
            err?.message === 'You are not authorised to access this page.'
          ) {
            this.router.navigate(['/unauthorized']);
          } else {
            this.showErrorToast(err?.message);
          }
        },
        complete: () => (this.loading = false),
      });
  }

  fetchUpcomingEvents(): void {
    this.loading = true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.httpService
      .get<APIResponse>('teacherevent/my-events?source=admin')
      .subscribe({
        next: (response) => {
          this.upcomingEvent = response.data?.items?.filter(
            (event: any) => event.eventStatus === 'Upcoming',
          );
        },
        error: (err) => {
          this.loading = false;
          console.error('Failed to load Event Types:', err);
          this.showErrorToast(err?.message);
        },
        complete: () => (this.loading = false),
      });
  }

  toggleTabs(tab: string): void {
    this.tableSegment = tab;
    if (this.tableComponent) {
      this.tableComponent.currentPage = 1;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.fetchEvents(tab);
  }

  getStatusClass(status: any): string {
    if (!status) return '';
    const normalized = String(status).trim().toLowerCase();
    switch (normalized) {
      case 'draft':
        return 'status-draft';
      case 'upcoming':
        return 'status-upcoming';
      case 'ongoing':
      case 'on today':
        return 'status-on-today';
      case 'completed':
      case 'event completed':
      case 'already held':
        return 'completed-text';
      case 'on hold':
      case 'onhold':
        return 'status-on-hold';
      case 'cancelled':
      case 'event cancelled':
      case 'deleted':
        return 'cancelled-text';
      default:
        return '';
    }
  }

  private normalizeAdminEventStatus(status: any): string {
    const normalized = String(status || '')
      .trim()
      .toLowerCase();
    switch (normalized) {
      case 'event completed':
      case 'completed':
        return 'Completed';
      case 'event cancelled':
      case 'cancelled':
        return 'Cancelled';
      case 'ongoing':
        return 'On Today';
      default:
        return status || '';
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'success-toast'],
    });
  }

  private showErrorToast(message: string | null | undefined): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['no-padding-snackbar', 'error-toast'],
    });
  }

  openWorkExperienceDialog(
    schoolToEdit: any = null,
    isEdit: boolean = false,
    width: string = '50.5rem',
  ): void {
    const dialogRef = this.dialog.open(WorkExperienceComponent, {
      width: width,
      maxWidth: '90vw',
      minWidth: '300px',
      data: { initialData: schoolToEdit, EditMode: isEdit },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const eventId = result.eventId || schoolToEdit?.eventId;
        this.router
          .navigate(['/events/event-list/view', eventId], {
            queryParams: { segment: 'teacher' },
          })
          .then((success) => {
            console.log('Navigation success:', success);
          })
          .catch((error) => {
            console.error('Navigation error:', error);
          });
      }
    });
  }
}
